// The world: a hub plus eight realms, each with its own theme, generator and monsters.

export type GenStyle = 'cave' | 'rooms' | 'open' | 'city' | 'islands' | 'hub' | 'arena';

export type FloorStyle = 'grass' | 'stone' | 'sand' | 'metal' | 'crystal' | 'asphalt' | 'ice' | 'warp' | 'hub';
export type WallStyle = 'trees' | 'brick' | 'sandstone' | 'bulkhead' | 'crystal' | 'building' | 'ice' | 'flesh' | 'hub';

export interface RealmDef {
  idx: number;
  id: string;
  name: string;
  blurb: string;
  level: number;
  style: GenStyle;
  floor: FloorStyle;
  wall: WallStyle;
  liquid: 'water' | 'lava' | 'toxic' | 'plasma' | 'ice' | 'void' | 'tar';
  /** 0 is pitch dark, 1 is bright daylight. */
  ambient: number;
  torch: number;
  /** Tints for floor & walls: [dark, mid, light, accent]. */
  floorPal: [string, string, string, string];
  wallPal: [string, string, string, string];
  liquidPal: [string, string, string];
  monsters: string[];
  elites: string[];
  boss: string;
  bossName: string;
  music: number;
  floors: number;
}

export const REALMS: RealmDef[] = [
  {
    idx: 0, id: 'verdant', name: 'The Verdant Wilds', blurb: 'A primeval forest grown wrong where a rift bled warp into its roots.',
    level: 1, style: 'cave', floor: 'grass', wall: 'trees', liquid: 'water', ambient: 0.62, torch: 11,
    floorPal: ['#00502c', '#006800', '#00a800', '#58d854'], wallPal: ['#002c14', '#005800', '#00a844', '#503000'], liquidPal: ['#0058f8', '#0078f8', '#3cbcfc'],
    monsters: ['slime', 'wolf', 'goblin', 'shroom'], elites: ['goblin', 'wolf'], boss: 'b_treant', bossName: 'Elderbark', music: 0, floors: 3,
  },
  {
    idx: 1, id: 'crypt', name: 'The Bone Crypts', blurb: 'Catacombs without end, their restless dead stirred by a rift below.',
    level: 6, style: 'rooms', floor: 'stone', wall: 'brick', liquid: 'tar', ambient: 0.1, torch: 9,
    floorPal: ['#404040', '#585858', '#7c7c7c', '#a0a0a0'], wallPal: ['#202028', '#484858', '#787890', '#a81000'], liquidPal: ['#181818', '#303018', '#504020'],
    monsters: ['skeleton', 'archer', 'zombie', 'ghost', 'necro'], elites: ['skeleton', 'zombie', 'necro'], boss: 'b_lich', bossName: 'Morvane', music: 1, floors: 3,
  },
  {
    idx: 2, id: 'dunes', name: 'The Mirage Dunes', blurb: 'A burning desert of glass and ruin, torn through a rift from beneath two suns.',
    level: 11, style: 'open', floor: 'sand', wall: 'sandstone', liquid: 'tar', ambient: 0.9, torch: 13,
    floorPal: ['#c88038', '#e0a050', '#f8c878', '#fce0a8'], wallPal: ['#6c3c10', '#a06020', '#d89848', '#f8b800'], liquidPal: ['#201408', '#402810', '#604020'],
    monsters: ['scorpion', 'mummy', 'wraith', 'beetle'], elites: ['mummy', 'scorpion'], boss: 'b_djinn', bossName: 'Azhar', music: 2, floors: 3,
  },
  {
    idx: 3, id: 'starship', name: 'The Derelict Starship', blurb: 'It fell through a rift from somewhere else, and its crew never stopped working.',
    level: 16, style: 'rooms', floor: 'metal', wall: 'bulkhead', liquid: 'plasma', ambient: 0.2, torch: 9,
    floorPal: ['#28303c', '#3c4858', '#586478', '#00e8d8'], wallPal: ['#141820', '#3c4458', '#7c8ca0', '#f83800'], liquidPal: ['#006868', '#00a8a8', '#58f8f8'],
    monsters: ['drone', 'robot', 'turret', 'xeno', 'spitter'], elites: ['robot', 'xeno', 'spitter'], boss: 'b_mech', bossName: 'OVERSEER-9', music: 3, floors: 3,
  },
  {
    idx: 4, id: 'prism', name: 'The Prismarch Caverns', blurb: 'Crystal veins drink raw warp from the rifts, and the light does not obey.',
    level: 21, style: 'cave', floor: 'crystal', wall: 'crystal', liquid: 'plasma', ambient: 0.28, torch: 10,
    floorPal: ['#2c1858', '#402880', '#6844a8', '#d8a8fc'], wallPal: ['#180c40', '#6844fc', '#b890fc', '#fc74b4'], liquidPal: ['#8000c0', '#c040f0', '#f8a0fc'],
    monsters: ['cgolem', 'wisp', 'cspider', 'construct'], elites: ['cgolem', 'cspider', 'construct'], boss: 'b_wyrm', bossName: 'Prismarch', music: 4, floors: 3,
  },
  {
    idx: 5, id: 'neon', name: 'The Neon Sprawl', blurb: 'A rain-slick city dragged through a rift from a future nobody ordered.',
    level: 26, style: 'city', floor: 'asphalt', wall: 'building', liquid: 'toxic', ambient: 0.38, torch: 11,
    floorPal: ['#141420', '#20202c', '#30303c', '#f878f8'], wallPal: ['#0c0c18', '#282838', '#484860', '#00e8d8'], liquidPal: ['#208000', '#58c000', '#b8f818'],
    monsters: ['thug', 'ninja', 'hovercop', 'cyborg'], elites: ['cyborg', 'ninja', 'thug'], boss: 'b_queen', bossName: 'Vex', music: 5, floors: 3,
  },
  {
    idx: 6, id: 'frost', name: 'Frostfell', blurb: 'A glacier that swallowed a kingdom, pulled through a rift with its dead still cold.',
    level: 31, style: 'cave', floor: 'ice', wall: 'ice', liquid: 'ice', ambient: 0.68, torch: 12,
    floorPal: ['#88b8d8', '#a8d0e8', '#c8e8f8', '#fcfcfc'], wallPal: ['#2c5880', '#5888b8', '#a4e4fc', '#fcfcfc'], liquidPal: ['#0040a0', '#0060d0', '#40a0f0'],
    monsters: ['icewolf', 'yeti', 'fwraith', 'icemage'], elites: ['yeti', 'icewolf', 'icemage'], boss: 'b_titan', bossName: 'Ymirax', music: 6, floors: 3,
  },
  {
    idx: 7, id: 'maw', name: 'The Warp Maw', blurb: 'The first rift, the wound in the world. Shards of every realm drift in the screaming dark.',
    level: 36, style: 'islands', floor: 'warp', wall: 'flesh', liquid: 'void', ambient: 0.2, torch: 10,
    floorPal: ['#300840', '#501068', '#801890', '#f83800'], wallPal: ['#180418', '#6c1048', '#b02868', '#f8b800'], liquidPal: ['#080010', '#180030', '#400060'],
    monsters: ['imp', 'demon', 'eye', 'warpling'], elites: ['demon', 'eye', 'imp'], boss: 'b_tyrant', bossName: 'The Warp Tyrant', music: 7, floors: 3,
  },
];

export const HUB_REALM: RealmDef = {
  idx: -1, id: 'hub', name: 'Waystation Nexus', blurb: 'The last safe place between the rifts.',
  level: 1, style: 'hub', floor: 'hub', wall: 'hub', liquid: 'plasma', ambient: 0.7, torch: 12,
  floorPal: ['#3c3048', '#50405c', '#685874', '#f8b800'], wallPal: ['#181420', '#403850', '#6c6480', '#3cbcfc'], liquidPal: ['#006868', '#00a8a8', '#58f8f8'],
  monsters: [], elites: [], boss: '', bossName: '', music: 8, floors: 1,
};

export const ARENA_REALM: RealmDef = {
  idx: -2, id: 'arena', name: 'The Blood Arena', blurb: 'Hero against hero. No quarter, no loot, all glory.',
  level: 1, style: 'arena', floor: 'stone', wall: 'brick', liquid: 'lava', ambient: 0.75, torch: 12,
  floorPal: ['#583030', '#704040', '#885050', '#f8b800'], wallPal: ['#201018', '#502838', '#884858', '#f83800'], liquidPal: ['#a81000', '#f83800', '#f8b800'],
  monsters: [], elites: [], boss: '', bossName: '', music: 1, floors: 1,
};

export const HUB_ZONE = 'hub';
export const ARENA_ZONE = 'arena';

export function zoneId(realm: number, floor: number): string {
  return `r${realm}f${floor}`;
}

export interface ZoneInfo {
  id: string;
  realm: RealmDef;
  floor: number;
  isBossFloor: boolean;
  level: number;
  safe: boolean;
  pvp: boolean;
  name: string;
}

export function parseZone(id: string): ZoneInfo {
  if (id === HUB_ZONE) {
    return { id, realm: HUB_REALM, floor: 0, isBossFloor: false, level: 1, safe: true, pvp: false, name: HUB_REALM.name };
  }
  if (id === ARENA_ZONE) {
    return { id, realm: ARENA_REALM, floor: 0, isBossFloor: false, level: 1, safe: false, pvp: true, name: ARENA_REALM.name };
  }
  const m = /^r(\d+)f(\d+)$/.exec(id);
  if (!m) throw new Error(`bad zone id ${id}`);
  const realm = REALMS[Number(m[1])];
  const floor = Number(m[2]);
  if (!realm || floor >= realm.floors) throw new Error(`bad zone id ${id}`);
  const isBossFloor = floor === realm.floors - 1;
  return {
    id,
    realm,
    floor,
    isBossFloor,
    level: realm.level + floor * 2,
    safe: false,
    pvp: false,
    name: isBossFloor ? `${realm.name} - Heart` : `${realm.name} - Level ${floor + 1}`,
  };
}

export function isValidZone(id: string): boolean {
  try {
    parseZone(id);
    return true;
  } catch {
    return false;
  }
}

export interface DifficultyDef {
  id: number;
  name: string;
  hp: number;
  dmg: number;
  xp: number;
  loot: number;
  /** Added to monster levels. */
  levels: number;
}

export const DIFFICULTIES: DifficultyDef[] = [
  { id: 0, name: 'Normal', hp: 1, dmg: 1, xp: 1, loot: 1, levels: 0 },
  { id: 1, name: 'Nightmare', hp: 1.8, dmg: 1.6, xp: 1.6, loot: 1.5, levels: 10 },
  { id: 2, name: 'Hell', hp: 3, dmg: 2.4, xp: 2.4, loot: 2.2, levels: 20 },
];
